/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const REVOKE_AUTHORIZATION_DISCRIMINATOR = new Uint8Array([
  222, 179, 207, 59, 191, 78, 24, 248,
]);

export function getRevokeAuthorizationDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REVOKE_AUTHORIZATION_DISCRIMINATOR,
  );
}

export type RevokeAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountAuthorization extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountAuthorization extends string
        ? WritableAccount<TAccountAuthorization>
        : TAccountAuthorization,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type RevokeAuthorizationInstructionData = {
  discriminator: ReadonlyUint8Array;
  nonce: Option<string>;
};

export type RevokeAuthorizationInstructionDataArgs = {
  nonce: OptionOrNullable<string>;
};

export function getRevokeAuthorizationInstructionDataEncoder(): Encoder<RevokeAuthorizationInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      [
        "nonce",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: REVOKE_AUTHORIZATION_DISCRIMINATOR,
    }),
  );
}

export function getRevokeAuthorizationInstructionDataDecoder(): Decoder<RevokeAuthorizationInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    [
      "nonce",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getRevokeAuthorizationInstructionDataCodec(): Codec<
  RevokeAuthorizationInstructionDataArgs,
  RevokeAuthorizationInstructionData
> {
  return combineCodec(
    getRevokeAuthorizationInstructionDataEncoder(),
    getRevokeAuthorizationInstructionDataDecoder(),
  );
}

export type RevokeAuthorizationInput<
  TAccountAgent extends string = string,
  TAccountAuthorization extends string = string,
  TAccountAuthority extends string = string,
> = {
  /** Agent revoking authorization */
  agent: Address<TAccountAgent>;
  /** Authorization account (PDA) */
  authorization: Address<TAccountAuthorization>;
  /** Authority (agent owner) */
  authority: TransactionSigner<TAccountAuthority>;
  nonce: RevokeAuthorizationInstructionDataArgs["nonce"];
};

export function getRevokeAuthorizationInstruction<
  TAccountAgent extends string,
  TAccountAuthorization extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RevokeAuthorizationInput<
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority
  >,
  config?: { programAddress?: TProgramAddress },
): RevokeAuthorizationInstruction<
  TProgramAddress,
  TAccountAgent,
  TAccountAuthorization,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agent: { value: input.agent ?? null, isWritable: false },
    authorization: { value: input.authorization ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.authorization),
      getAccountMeta(accounts.authority),
    ],
    data: getRevokeAuthorizationInstructionDataEncoder().encode(
      args as RevokeAuthorizationInstructionDataArgs,
    ),
    programAddress,
  } as RevokeAuthorizationInstruction<
    TProgramAddress,
    TAccountAgent,
    TAccountAuthorization,
    TAccountAuthority
  >);
}

export type ParsedRevokeAuthorizationInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent revoking authorization */
    agent: TAccountMetas[0];
    /** Authorization account (PDA) */
    authorization: TAccountMetas[1];
    /** Authority (agent owner) */
    authority: TAccountMetas[2];
  };
  data: RevokeAuthorizationInstructionData;
};

export function parseRevokeAuthorizationInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRevokeAuthorizationInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agent: getNextAccount(),
      authorization: getNextAccount(),
      authority: getNextAccount(),
    },
    data: getRevokeAuthorizationInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
